import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  FILTRE_SANS_MOTIF,
  MOVEMENT_SOURCES,
  MOVEMENT_SOURCE_LABELS,
  createValidationSchema,
  hasAtLeastRole,
  typeSuggere,
  type ListRestantQueryInput,
  type MouvementRestantVue,
  type SensTri,
  type TriRestant,
} from '@kn/shared';
import { api } from '../api/client';
import { messageErreur } from '../api/error-message';
import { useAuth } from '../auth/auth-context';
import { FiltreMultiple } from '../components/FiltreMultiple';
import { LibelleDae } from '../components/LibelleDae';
import { LegendeVert } from '../components/LegendeVert';

const CLE = ['restant'] as const;
const HAUTEUR_LIGNE = 34;

/** Les colonnes triables, dans l'ordre où elles s'affichent. */
const COLONNES: Array<{ cle: TriRestant; libelle: string; titre?: string; droite?: boolean }> = [
  { cle: 'cote', libelle: 'Côté' },
  { cle: 'source', libelle: 'Source' },
  { cle: 'codpro', libelle: 'Article' },
  { cle: 'jour', libelle: 'Jour' },
  { cle: 'reference', libelle: 'Référence' },
  { cle: 'motif', libelle: 'Motif' },
  { cle: 'uvc', libelle: 'UVC', titre: 'Trier par quantité (UVC)', droite: true },
];

/**
 * Le couple court d'une ligne (« 20/FAB ») et son infobulle (lot 0.14.0). Le
 * libellé vient de la table de KN ; quand KN ne l'a pas fourni, l'écran le dit
 * plutôt que de laisser croire à une panne.
 */
function coupleCourt(ligne: MouvementRestantVue): { texte: string; titre?: string } {
  if (!ligne.codeMouvement) return { texte: ligne.motif ?? '—' };
  const couple = `${ligne.codeMouvement}/${ligne.motif ?? ''}`;
  return { texte: couple, titre: `${couple} — ${ligne.libelleMouvement ?? 'libellé non fourni'}` };
}

/**
 * La référence d'une ligne, suivie de son libellé DAE (ligne douane, lot
 * 0.14.0) : il vient en dernier, et c'est lui qui se tronque le premier.
 */
function CelluleReference({ ligne }: { ligne: MouvementRestantVue }): ReactElement {
  const avecDae = (ligne.libelleDaeLivraison ?? ligne.libelleDaeReception) !== null;
  return (
    <span>
      {ligne.reference !== null ? <span>{ligne.reference}</span> : !avecDae && '—'}
      {ligne.estOde && <span className="etiquette">ODE</span>}
      <LibelleDae livraison={ligne.libelleDaeLivraison} reception={ligne.libelleDaeReception} />
    </span>
  );
}

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function formaterDate(iso: string): string {
  const [annee, mois, jour] = iso.split('-');
  return jour && mois && annee ? `${jour}/${mois}/${annee}` : iso;
}

/**
 * « Restant à expliquer » — règle 17.
 *
 * Tout ce qu'aucune des trois explications ne couvre : ni apparié par
 * référence, ni couvert par une validation, ni déjà rapproché à la main. Sur les
 * données réelles, cela représente des dizaines de milliers de lignes — d'où la
 * pagination et la virtualisation, qui ne sont pas un confort.
 *
 * **Rapprocher explique, cela ne solde pas.** Les lignes quittent cet écran ;
 * aucun cumul, aucun indicateur ne bouge. Quand une sélection se neutralise sur
 * une seule clé suivie, l'outil le dit et propose une validation pré-remplie —
 * il ne la fait pas.
 */
export function RestantPage(): ReactElement {
  const { user } = useAuth();
  const peutRapprocher = user ? hasAtLeastRole(user.role, 'OPERATEUR') : false;
  const queryClient = useQueryClient();
  const conteneur = useRef<HTMLDivElement>(null);

  const [page, setPage] = useState(1);
  const [cote, setCote] = useState('');
  const [source, setSource] = useState('');
  const [codpros, setCodpros] = useState('');
  const [uvc, setUvc] = useState('');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [sens, setSens] = useState('');
  const [masquerOde, setMasquerOde] = useState(false);
  const [motifs, setMotifs] = useState<string[]>([]);
  // Périmètre 3A-bis (N19) : même filtre, même défaut que l'écran Écarts.
  const [inclureHorsControle, setInclureHorsControle] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [triPar, setTriPar] = useState<TriRestant>('impact');
  const [triSens, setTriSens] = useState<SensTri>('DESC');

  const [selection, setSelection] = useState<string[]>([]);
  const [typeId, setTypeId] = useState('');
  /**
   * Le type pre-selectionne par la suggestion DAI (lot 0.14.0), tant que
   * l'operatrice n'a pas choisi elle-meme : une suggestion, jamais imposee.
   */
  const [typeAuto, setTypeAuto] = useState<string | null>(null);
  const [typeTouche, setTypeTouche] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const [residuelConfirme, setResiduelConfirme] = useState(false);
  const [detailOuvert, setDetailOuvert] = useState(false);
  /**
   * La validation que l'outil propose **apres** un rapprochement qui se
   * neutralise. Retenue ici parce que la selection, elle, vient d'etre videe :
   * sans cela la proposition disparaîtrait au moment precis ou elle a un sens.
   */
  const [validationProposee, setValidationProposee] = useState<{
    adresse: string;
    cleSuivie: string;
    codpros: string[];
    commentaire: string;
    /** Nulle a net nul ; l'effet plafonne pour un type qui propose (DAI). */
    quantite: string;
    parLeType: boolean;
  } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const filtres: ListRestantQueryInput = useMemo(
    () => ({
      page,
      pageSize: 200,
      ...(cote ? { cote: cote as 'WMS' | 'DOUANE' } : {}),
      ...(source ? { source: source as (typeof MOVEMENT_SOURCES)[number] } : {}),
      ...(codpros.trim() ? { codpros: codpros.trim() } : {}),
      ...(uvc.trim() !== '' && Number.isFinite(Number(uvc)) ? { uvc: Number(uvc) } : {}),
      ...(du ? { du } : {}),
      ...(au ? { au } : {}),
      ...(sens ? { sens: sens as 'ENTREE' | 'SORTIE' } : {}),
      ...(motifs.length > 0 ? { motifs } : {}),
      masquerOde,
      inclureHorsControle,
      ...(recherche.trim() ? { search: recherche.trim() } : {}),
      triPar,
      triSens,
    }),
    [
      page,
      cote,
      source,
      codpros,
      uvc,
      du,
      au,
      sens,
      motifs,
      masquerOde,
      inclureHorsControle,
      recherche,
      triPar,
      triSens,
    ],
  );

  const restant = useQuery({
    queryKey: [...CLE, filtres],
    queryFn: () => api.restant(filtres),
  });

  const couples = useQuery({
    queryKey: ['restant-motifs'],
    queryFn: () => api.couplesMotifs(),
    staleTime: 5 * 60 * 1000,
  });
  const optionsMotifs = useMemo(
    () => [
      { valeur: FILTRE_SANS_MOTIF, libelle: '(sans motif)' },
      ...(couples.data ?? []).map((c) => ({
        valeur: `${c.codeMouvement}/${c.motif}`,
        libelle: `${c.codeMouvement}/${c.motif} — ${c.libelle ?? 'libellé non fourni'}`,
      })),
    ],
    [couples.data],
  );

  const types = useQuery({
    queryKey: ['reconciliation-types', 'actifs'],
    queryFn: () => api.reconciliationTypes(true),
    staleTime: 5 * 60 * 1000,
  });

  // Le type choisi décide de la proposition, et d'un rapprochement d'une
  // seule ligne (lot 0.14.0).
  const typeChoisi = types.data?.find((t) => t.id === typeId);

  // Le résumé fait foi : il est recalculé en base, pas additionné à l'écran.
  const resume = useQuery({
    queryKey: ['restant', 'resume', selection, typeId],
    queryFn: () => api.resumeSelection(selection, typeId || undefined),
    enabled: selection.length > 0,
  });

  const rapprocher = useMutation({
    mutationFn: () =>
      api.rapprocher({
        mouvementIds: selection,
        typeId,
        commentaire: commentaire.trim(),
        residuelConfirme,
      }),
    onSuccess: (fait) => {
      // L'API rend la proposition avec le rapprochement, calculee sur la
      // selection relue : rapprocher explique, valider solde, et c'est ici que
      // le second geste s'offre.
      const proposition = fait.propositionValidation;
      setSelection([]);
      setCommentaire('');
      setResiduelConfirme(false);
      setErreur(null);
      setValidationProposee(
        proposition
          ? {
              adresse: proposition.adresse,
              cleSuivie: proposition.cleSuivie,
              codpros: proposition.codpros,
              quantite: proposition.quantite,
              parLeType: proposition.parLeType,
              commentaire: proposition.parLeType
                ? `[${fait.typeLibelle}] ${fait.commentaire}`
                : `Rapprochement manuel du ${new Date().toLocaleDateString('fr-FR')} ` +
                  `(${fait.typeLibelle}) : ${fait.lignes.length} mouvements se neutralisent. ` +
                  fait.commentaire,
            }
          : null,
      );
      setMessage(
        `Rapprochement enregistré : ${fait.lignes.length} mouvement(s), type « ${fait.typeLibelle} »` +
          (Number(fait.residuel) === 0 ? '.' : `, résiduel ${formaterNombre(fait.residuel)} UVC.`),
      );
      void queryClient.invalidateQueries({ queryKey: CLE });
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  // Memoisee : sans cela le `?? []` rend un tableau neuf a chaque rendu, et
  // tout ce qui en depend (les identifiants affiches) se recalculerait sans fin.
  /**
   * Le second geste : **solder**.
   *
   * Volontairement une mutation distincte, declenchee par un clic distinct.
   * L'API dit que la selection se neutralise ; elle ne solde rien. Rapprocher
   * explique, seule une validation sort l'ecart des vues (regle 17).
   */
  const valider = useMutation({
    mutationFn: async () => {
      if (!validationProposee) throw new Error('Aucune validation proposée.');
      const ecart = await api.ecartParAdresse(validationProposee.adresse);
      return api.validerEcart(ecart.id, {
        type: 'TEMPORAIRE',
        quantite: validationProposee.quantite,
        commentaire: validationProposee.commentaire,
      });
    },
    onSuccess: () => {
      setMessage(
        validationProposee?.parLeType
          ? `Validation temporaire enregistrée : ${formaterNombre(validationProposee.quantite)} UVC ` +
              `sur ${validationProposee.cleSuivie}.`
          : "Validation enregistrée. Le rapprochement est tracé, l'écart est inchangé.",
      );
      setValidationProposee(null);
      setErreur(null);
      void queryClient.invalidateQueries({ queryKey: CLE });
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const lignes: MouvementRestantVue[] = useMemo(() => restant.data?.items ?? [], [restant.data]);
  const total = restant.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / 200));

  const virtualiseur = useVirtualizer({
    count: lignes.length,
    getScrollElement: () => conteneur.current,
    estimateSize: () => HAUTEUR_LIGNE,
    overscan: 12,
  });

  /**
   * Un clic trie ; un second inverse le sens.
   *
   * Changer de colonne repart en ASC : c'est ce qu'on attend quand on demande
   * « trie par article », et non l'ordre hérité de la colonne précédente.
   */
  const trier = (colonne: TriRestant): void => {
    setPage(1);
    if (colonne === triPar) {
      setTriSens((sensActuel) => (sensActuel === 'ASC' ? 'DESC' : 'ASC'));
      return;
    }
    setTriPar(colonne);
    setTriSens('ASC');
  };

  const basculer = (id: string): void =>
    setSelection((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  /**
   * La case d'en-tête porte sur les lignes **affichées**, pas sur le filtre
   * entier.
   *
   * Le filtre compte souvent des dizaines de milliers de mouvements, quand la
   * page en montre deux cents : une case qui prétendrait tout prendre
   * promettrait un geste que personne ne veut faire, et que le rapprochement ne
   * saurait pas porter. Ce qu'on a coché sur une autre page est **conservé** —
   * la sélection se construit page après page.
   */
  const idsAffiches = useMemo(() => lignes.map((ligne) => ligne.id), [lignes]);
  const nbChoisiesAffichees = idsAffiches.filter((id) => selection.includes(id)).length;
  const toutesChoisies = idsAffiches.length > 0 && nbChoisiesAffichees === idsAffiches.length;

  // Une sélection partielle n'est ni cochee ni vide : `indeterminate` ne
  // s'écrit qu'en JavaScript, d'ou cette référence.
  const caseTout = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (caseTout.current) {
      caseTout.current.indeterminate = nbChoisiesAffichees > 0 && !toutesChoisies;
    }
  }, [nbChoisiesAffichees, toutesChoisies]);

  // Suggestion du type DAI : seulement si toutes les lignes choisies sont
  // connues de la page — une ligne d'une autre page ne dit pas son libellé, et
  // on ne suggère pas sur ce qu'on ne voit pas.
  const suggestion = useMemo(() => {
    const choisies = selection.map((id) => lignes.find((l) => l.id === id));
    if (choisies.some((l) => l === undefined)) return null;
    return typeSuggere(choisies as MouvementRestantVue[], types.data ?? []);
  }, [selection, lignes, types.data]);

  useEffect(() => {
    if (selection.length === 0) setTypeTouche(false);
  }, [selection.length]);

  useEffect(() => {
    if (suggestion && !typeTouche) {
      setTypeId(suggestion);
      setTypeAuto(suggestion);
    } else if (!suggestion && typeAuto !== null) {
      // La suggestion ne tient plus : on retire ce qu'elle avait posé, pas un
      // choix de l'opératrice.
      setTypeId((courant) => (courant === typeAuto ? '' : courant));
      setTypeAuto(null);
    }
  }, [suggestion, typeTouche, typeAuto]);

  const basculerTout = (): void =>
    setSelection((ids) =>
      toutesChoisies
        ? ids.filter((id) => !idsAffiches.includes(id))
        : [...ids, ...idsAffiches.filter((id) => !ids.includes(id))],
    );

  function soumettre(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setErreur(null);
    setMessage(null);
    // Une ligne seule ne se rapproche qu'avec un type qui propose la
    // validation : la même règle qu'à l'API, dite avant l'envoi.
    if (selection.length < 2 && !typeChoisi?.proposeValidation) {
      setErreur('Un rapprochement relie au moins deux mouvements.');
      return;
    }
    if (!typeId) {
      setErreur('Choisissez un type de rapprochement.');
      return;
    }
    if (commentaire.trim() === '') {
      setErreur('Le commentaire est obligatoire.');
      return;
    }
    rapprocher.mutate();
  }

  const net = resume.data?.net ?? '0';
  const residuel = Number(net) !== 0;
  // Même règle que l'API (règle 14), lue sur le schéma partagé : dite à
  // l'écran, plutôt qu'en erreur.
  const commentaireValidation = createValidationSchema.shape.commentaire.safeParse(
    validationProposee?.commentaire ?? '',
  );
  const commentaireTropCourt = !commentaireValidation.success;

  return (
    <>
      <h1>Restant à expliquer</h1>
      <p className="sous-titre">
        Les mouvements qu&apos;<strong>aucune explication</strong> ne couvre : ni appariés par
        référence, ni couverts par une validation, ni déjà rapprochés à la main. Rapprocher{' '}
        <strong>explique</strong> — cela ne solde pas : les cumuls et les indicateurs ne bougent
        pas.
      </p>
      <LegendeVert />

      {message && (
        <p className="alerte alerte--info" role="status">
          {message}
        </p>
      )}

      {/*
        La proposition de validation : **un second geste, jamais automatique**.
        L'outil a constate que la selection se neutralise sur une seule cle ; il
        le dit, pre-remplit, et attend. Rapprocher explique, valider solde — et
        c'est une personne qui solde.
      */}
      {validationProposee && (
        <section className="proposition-validation" aria-label="Validation proposée">
          {validationProposee.parLeType ? (
            <>
              {/*
                Type qui propose la validation (DAI, lot 0.14.0) : la quantite
                est l'effet du rapprochement sur l'ecart, plafonne a son reste
                ouvert. En couleur neutre : un chiffre signe ne se colore pas.
              */}
              <p className="proposition-validation__titre">
                Valider{' '}
                <strong className="nombre">
                  {formaterNombre(validationProposee.quantite)} UVC
                </strong>{' '}
                sur <strong>{validationProposee.cleSuivie}</strong> (
                {validationProposee.codpros.join(', ')}) ?
              </p>
              <p className="sous-titre">
                C'est l'effet de ce rapprochement sur l'écart, dans la limite de ce qui reste
                ouvert. La validation est temporaire, et reste un geste distinct.
              </p>
            </>
          ) : (
            <>
              <p className="proposition-validation__titre">
                Cette sélection se neutralise sur <strong>{validationProposee.cleSuivie}</strong> (
                {validationProposee.codpros.join(', ')}). Voulez-vous <strong>valider</strong>{' '}
                l'écart ?
              </p>
              {/*
                Une validation a quantite 0 trace l'acte : elle ne retire rien du
                residuel, donc rien de la vue quotidienne (lot 0.14.0, ticket 02).
              */}
              <p className="sous-titre">
                Le rapprochement est tracé, l'écart est inchangé. Valider est un geste distinct, qui
                inscrit cet acte dans l'historique de l'écart.
              </p>
            </>
          )}
          <label className="champ champ--large" htmlFor="proposition-commentaire">
            <span>Commentaire de validation</span>
            <textarea
              id="proposition-commentaire"
              rows={2}
              value={validationProposee.commentaire}
              onChange={(e) =>
                setValidationProposee((p) => (p ? { ...p, commentaire: e.target.value } : p))
              }
            />
          </label>
          {!commentaireValidation.success && (
            <p id="proposition-commentaire-aide" className="sous-titre">
              {commentaireValidation.error.issues[0]?.message}
            </p>
          )}
          <div className="barre-actions">
            <button
              type="button"
              className="bouton"
              disabled={!peutRapprocher || valider.isPending || commentaireTropCourt}
              aria-describedby={commentaireTropCourt ? 'proposition-commentaire-aide' : undefined}
              onClick={() => valider.mutate()}
            >
              {valider.isPending ? 'Validation…' : "Valider l'écart (temporaire)"}
            </button>
            <button
              type="button"
              className="bouton bouton--secondaire"
              onClick={() => setValidationProposee(null)}
            >
              Ne pas valider
            </button>
            <a className="bouton bouton--lien" href={`/ecarts/${validationProposee.adresse}`}>
              Ouvrir la fiche
            </a>
          </div>
        </section>
      )}
      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}

      <div className="carte">
        <div className="barre-actions">
          <label className="champ" htmlFor="restant-cote">
            <span>Côté</span>
            <select id="restant-cote" value={cote} onChange={(e) => { setCote(e.target.value); setPage(1); }}>
              <option value="">Les deux</option>
              <option value="WMS">Physique (WMS)</option>
              <option value="DOUANE">Comptabilité matières</option>
            </select>
          </label>
          <label className="champ" htmlFor="restant-source">
            <span>Source</span>
            <select id="restant-source" value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }}>
              <option value="">Toutes</option>
              {MOVEMENT_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {MOVEMENT_SOURCE_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="champ champ--large" htmlFor="restant-codpros">
            <span>Articles</span>
            <input
              id="restant-codpros"
              type="text"
              placeholder="433592 433289 433487"
              title="Un ou plusieurs codes, séparés par des espaces."
              value={codpros}
              onChange={(e) => {
                setCodpros(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="champ" htmlFor="restant-uvc">
            <span>Quantité (UVC)</span>
            <input
              id="restant-uvc"
              type="number"
              step="any"
              placeholder="480"
              title="Saisir 480 rend les +480 comme les −480 : la contrepartie porte le même nombre, au signe près."
              value={uvc}
              onChange={(e) => {
                setUvc(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="champ" htmlFor="restant-du">
            <span>Du</span>
            <input id="restant-du" type="date" value={du} onChange={(e) => { setDu(e.target.value); setPage(1); }} />
          </label>
          <label className="champ" htmlFor="restant-au">
            <span>Au</span>
            <input id="restant-au" type="date" value={au} onChange={(e) => { setAu(e.target.value); setPage(1); }} />
          </label>
          <label className="champ" htmlFor="restant-sens">
            <span>Sens</span>
            <select id="restant-sens" value={sens} onChange={(e) => { setSens(e.target.value); setPage(1); }}>
              <option value="">Les deux</option>
              <option value="ENTREE">Entrées</option>
              <option value="SORTIE">Sorties</option>
            </select>
          </label>
          <label className="champ" htmlFor="restant-recherche">
            <span>Rechercher</span>
            <input
              id="restant-recherche"
              type="search"
              placeholder="Référence, code, libellé ou DAE"
              value={recherche}
              onChange={(e) => { setRecherche(e.target.value); setPage(1); }}
            />
          </label>
          <FiltreMultiple
            titre="Motifs"
            libelleRecherche="Rechercher un motif"
            options={optionsMotifs}
            choisis={motifs}
            onChange={(valeurs) => { setMotifs(valeurs); setPage(1); }}
          />
          <label className="champ" htmlFor="restant-ode">
            <span>Mouvements manuels (ODE)</span>
            <select
              id="restant-ode"
              value={masquerOde ? 'masques' : 'affiches'}
              onChange={(e) => { setMasquerOde(e.target.value === 'masques'); setPage(1); }}
            >
              <option value="affiches">Affichés</option>
              <option value="masques">Masqués</option>
            </select>
          </label>
          {/*
            Identique à l'écran Écarts (libellé, place, valeurs). Masqués : côté
            physique, seuls les mouvements des articles de la fiche ALCDS et de
            leurs familles ; la comptabilité matières, elle, est toujours là.
            L'aide le dit (QD-011), par le même mécanisme que les autres
            filtres de l'écran : l'infobulle native.
          */}
          <label className="champ" htmlFor="restant-hors-controle">
            <span>Articles hors périmètre</span>
            <select
              id="restant-hors-controle"
              title="Les lignes de compta matières restent toujours affichées, même pour un article hors périmètre."
              value={inclureHorsControle ? 'oui' : 'non'}
              onChange={(e) => { setInclureHorsControle(e.target.value === 'oui'); setPage(1); }}
            >
              <option value="non">Masqués</option>
              <option value="oui">Affichés</option>
            </select>
          </label>
        </div>

        {restant.isPending && <p className="chargement">Chargement du restant…</p>}
        {restant.isError && (
          <p className="alerte alerte--erreur" role="alert">
            {messageErreur(restant.error)}
          </p>
        )}

        {restant.data && (
          <>
            <div className="totaux">
              <div>
                <span>Mouvements sans explication</span>
                <strong>{total.toLocaleString('fr-FR')}</strong>
              </div>
              <div>
                <span>Affichés</span>
                <strong>{lignes.length.toLocaleString('fr-FR')}</strong>
              </div>
              <div>
                <span>Sélectionnés</span>
                <strong className={selection.length > 0 ? 'nombre nombre--succes' : 'nombre'}>
                  {selection.length.toLocaleString('fr-FR')}
                </strong>
              </div>
              <div>
                <span>Fenêtre d&apos;appariement</span>
                <strong>± {restant.data.fenetreAppariementJours} j</strong>
              </div>
            </div>

            {total === 0 && (
              <p className="alerte alerte--info" role="status">
                Rien à expliquer sur ce filtre. Tout est apparié, validé ou déjà rapproché.
              </p>
            )}

            {lignes.length > 0 && (
              <>
                {triPar === 'impact' ? (
                  <p className="restant-ordre" role="status">
                    Ordre par défaut : <strong>les références qui pèsent le plus
                    d'abord</strong>, leurs lignes groupées. Cliquez une colonne pour
                    ordonner autrement.
                  </p>
                ) : (
                  <p className="restant-ordre">
                    Trié sur une colonne.{' '}
                    <button
                      type="button"
                      className="bouton bouton--lien"
                      onClick={() => {
                        setTriPar('impact');
                        setTriSens('DESC');
                        setPage(1);
                      }}
                    >
                      Revenir à l'ordre par défaut
                    </button>
                  </p>
                )}

                <div className="restant-grille grille-entetes" role="row">
                  <span>
                    <input
                      type="checkbox"
                      ref={caseTout}
                      checked={toutesChoisies}
                      disabled={!peutRapprocher}
                      aria-label={`${toutesChoisies ? 'Désélectionner' : 'Sélectionner'} toutes les lignes affichées`}
                      title={
                        `${toutesChoisies ? 'Tout désélectionner' : 'Tout sélectionner'} : ` +
                        `les ${lignes.length.toLocaleString('fr-FR')} ligne(s) affichée(s)` +
                        (total > lignes.length
                          ? `, pas les ${total.toLocaleString('fr-FR')} du filtre entier.`
                          : '.')
                      }
                      onChange={basculerTout}
                    />
                  </span>
                  {COLONNES.map((colonne) => (
                    <span
                      key={colonne.cle}
                      className={colonne.droite ? 'droite' : undefined}
                      role="columnheader"
                      aria-sort={
                        triPar === colonne.cle
                          ? triSens === 'ASC'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <button
                        type="button"
                        className={`tri${triPar === colonne.cle ? ' tri--actif' : ''}`}
                        onClick={() => trier(colonne.cle)}
                        title={colonne.titre ?? `Trier par ${colonne.libelle.toLowerCase()}`}
                      >
                        {colonne.libelle}
                        <span className="tri__fleche" aria-hidden="true">
                          {triPar === colonne.cle ? (triSens === 'ASC' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    </span>
                  ))}
                </div>

                <div className="grille-virtuelle grille-virtuelle--restant" ref={conteneur}>
                  <div style={{ height: `${virtualiseur.getTotalSize()}px`, position: 'relative' }}>
                    {virtualiseur.getVirtualItems().map((element) => {
                      const ligne = lignes[element.index]!;
                      const choisi = selection.includes(ligne.id);
                      return (
                        <div
                          key={ligne.id}
                          className={`restant-grille grille-ligne${choisi ? ' grille-ligne--choisie' : ''}`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${element.size}px`,
                            transform: `translateY(${element.start}px)`,
                          }}
                        >
                          <span>
                            <input
                              type="checkbox"
                              checked={choisi}
                              disabled={!peutRapprocher}
                              aria-label={`Sélectionner ${ligne.codpro} du ${formaterDate(ligne.jour)}`}
                              onChange={() => basculer(ligne.id)}
                            />
                          </span>
                          <span>
                            <span
                              className={`cote cote--${ligne.cote === 'WMS' ? 'wms' : 'douane'}`}
                            >
                              {ligne.cote === 'WMS' ? 'Physique' : 'Douane'}
                            </span>
                          </span>
                          <span title={MOVEMENT_SOURCE_LABELS[ligne.source]}>{ligne.source}</span>
                          <span title={ligne.designation ?? undefined}>{ligne.codpro}</span>
                          <span>{formaterDate(ligne.jour)}</span>
                          <CelluleReference ligne={ligne} />
                          <span title={coupleCourt(ligne).titre}>{coupleCourt(ligne).texte}</span>
                          <span className="droite">
                            {formaterNombre(ligne.uvc)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="barre-actions">
                  <button
                    type="button"
                    className="bouton bouton--secondaire"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Page précédente
                  </button>
                  <span className="sous-titre">
                    Page {page} sur {pages.toLocaleString('fr-FR')}
                  </span>
                  <button
                    type="button"
                    className="bouton bouton--secondaire"
                    disabled={page >= pages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Page suivante
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {selection.length > 0 && (
        /*
         * Barre collante plutôt que carte en bas de page.
         *
         * Livrée sous le tableau, la sélection tombait à 700 pixels sous la
         * ligne qu'on venait de cocher : on choisissait à l'aveugle, puis on
         * descendait vérifier. Le résumé doit rester sous les yeux **pendant**
         * la sélection — c'est ce que « permanent » veut dire.
         */
        <section className="selection-collante" aria-label="Sélection en cours">
          <div className="selection-collante__resume">
            <span className="selection-collante__compte">
              <strong>{resume.data?.lignes ?? selection.length}</strong> ligne(s)
            </span>
            <span>
              Entrées <strong>{formaterNombre(resume.data?.entrees ?? '0')}</strong>
            </span>
            <span>
              Sorties <strong>{formaterNombre(resume.data?.sorties ?? '0')}</strong>
            </span>
            <span className="selection-collante__net">
              Net{' '}
              <strong className={residuel ? 'nombre nombre--alerte' : 'nombre nombre--succes'}>
                {formaterNombre(net)}
              </strong>
            </span>
            {resume.data?.propositionValidation && (
              <span
                // Une quantite proposee reste en couleur neutre (QF-006) ; le
                // vert ne dit que « se neutralise ».
                className={
                  resume.data.propositionValidation.parLeType
                    ? 'etiquette'
                    : 'etiquette etiquette--actif'
                }
                title="Après le rapprochement, une validation pré-remplie vous sera proposée — un geste distinct, à confirmer."
              >
                {resume.data.propositionValidation.parLeType
                  ? `Validation proposée : ${formaterNombre(resume.data.propositionValidation.quantite)} UVC sur ${resume.data.propositionValidation.cleSuivie}`
                  : `Se neutralise sur ${resume.data.propositionValidation.cleSuivie}`}
              </span>
            )}
            <button
              type="button"
              className="bouton bouton--lien"
              onClick={() => setDetailOuvert((ouvert) => !ouvert)}
              aria-expanded={detailOuvert}
            >
              {detailOuvert ? 'Masquer le détail' : 'Détail par article'}
            </button>
          </div>

          {detailOuvert && resume.data && resume.data.parArticle.length > 0 && (
            <div className="selection-collante__detail">
              <table className="tableau">
                <thead>
                  <tr>
                    <th scope="col">Article</th>
                    <th scope="col">Lignes</th>
                    <th scope="col">UVC</th>
                  </tr>
                </thead>
                <tbody>
                  {resume.data.parArticle.map((a) => (
                    <tr key={a.codpro}>
                      <td>{a.codpro}</td>
                      <td className="droite">{a.lignes}</td>
                      <td className="droite">
                        {formaterNombre(a.uvc)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {peutRapprocher ? (
            <form className="selection-collante__acte" onSubmit={soumettre}>
              <label className="champ" htmlFor="rappro-type">
                <span>Type</span>
                <select
                  id="rappro-type"
                  value={typeId}
                  onChange={(e) => {
                    setTypeId(e.target.value);
                    setTypeTouche(true);
                    setTypeAuto(null);
                  }}
                >
                  <option value="">Choisir…</option>
                  {(types.data ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.libelle}
                    </option>
                  ))}
                </select>
              </label>
              {typeAuto !== null && typeId === typeAuto && (
                <span
                  className="etiquette"
                  title="Les lignes choisies portent un libellé DAE « DAI » : le type DAI est proposé. Vous pouvez en choisir un autre."
                >
                  suggéré
                </span>
              )}
              <label className="champ champ--large" htmlFor="rappro-commentaire">
                <span>Commentaire</span>
                <input
                  id="rappro-commentaire"
                  type="text"
                  maxLength={2000}
                  placeholder="Ce qui relie ces mouvements"
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                />
              </label>
              {residuel && (
                <div className="selection-collante__residuel">
                  <span className="selection-collante__alerte">
                    Ne se neutralise pas : {formaterNombre(net)} UVC
                  </span>
                  <label htmlFor="rappro-residuel">
                    <input
                      id="rappro-residuel"
                      type="checkbox"
                      checked={residuelConfirme}
                      onChange={(e) => setResiduelConfirme(e.target.checked)}
                    />
                    Rapprocher malgré ce résiduel
                  </label>
                </div>
              )}
              <button type="submit" className="bouton" disabled={rapprocher.isPending}>
                {rapprocher.isPending ? 'Enregistrement…' : 'Rapprocher'}
              </button>
              <button
                type="button"
                className="bouton bouton--secondaire"
                onClick={() => setSelection([])}
              >
                Vider
              </button>
            </form>
          ) : (
            <p className="sous-titre">
              Lecture seule : le rapprochement demande le rôle opérateur.
            </p>
          )}
        </section>
      )}
    </>
  );
}
